import { Component, Host, Prop, State, Watch, Element, h } from '@stencil/core';
import { Store } from '../../components';
import { storeService } from '../../utils/services/storeService';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { ASL_StoreInfoCardProps } from '../aico-store-locator-store-info-card/aico-store-locator-store-info-card';
import { brandService } from '../../utils/services/brandService';
import { z } from 'zod';
import { TranslationsProps } from '../../utils/services/translationService';

export const ASL_MapInfoWindowProps = z.object({
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),
  ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
});
export type ASL_MapInfoWindowProps = z.infer<typeof ASL_MapInfoWindowProps>;

@Component({
  tag: 'aico-store-locator-map-info-window',
  styleUrl: 'aico-store-locator-map-info-window.css',
  shadow: true,
})
export class AicoStoreLocatorMapInfoWindow {

  @Prop() store!: Store;

  @Prop() componentColors?: ComponentColors;

  @Prop() props?: ASL_MapInfoWindowProps;

  @State() currentStore?: Store;
  @State() previousStore?: Store;
  @State() animationClass: string = '';


  @Element() el!: HTMLElement;

  componentWillLoad() {
    if (this.props) {
      const parsedProps = ASL_MapInfoWindowProps.parse(this.props);
      this.componentColors = parsedProps.componentColors ?? this.componentColors;
    }

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }
    this.currentStore = this.store;
    this.animationClass = 'animate-slide-in-bottom';
  }

  @Watch('store')
  storeChanged(newStore: Store, oldStore: Store) {
    if (!oldStore) return;
    if (newStore.id === oldStore.id) return;

    this.previousStore = this.currentStore;
    this.currentStore = newStore;
    this.animationClass = 'animate-slide-horizontal-in';
    setTimeout(() => {
      this.previousStore = undefined;
      this.animationClass = '';
    }, 500);
  }

  private openDetailsView() {
    storeService.setSelectedStore(this.store);
  }

  render() {
    return (
      <Host class="right-0 bottom-0 left-0 absolute overflow-hidden">
        <div class="z-50 relative my-4 mr-4 ml-2 animate-slide-in-bottom">
          <div
            class="relative bg-[--background-color] shadow-lg p-1 rounded-lg overflow-hidden"
            onClick={() => this.openDetailsView()}
          >
            {this.previousStore && (
              <div class="absolute inset-0 animate-slide-horizontal-out">
                <aico-store-locator-store-info-card
                  refKey={this.previousStore.id}
                  organisationName={this.previousStore.organisationName || 'Unknown Store'}
                  addressLine={this.previousStore.addressLine1 || ''}
                  zipCode={this.previousStore.zipCode || ''}
                  city={this.previousStore.city || ''}
                  distance={this.previousStore.distance}
                  brandsLogos={brandService.getLogosOrderedByPriority(this.previousStore?.crmAddress?.storeLocatorBrandIds)}
                  props={this.props?.ASL_StoreInfoCardProps}
                />
              </div>
            )}
            {this.currentStore && (
              <div class={`relative ${this.animationClass}`}>
                <aico-store-locator-store-info-card
                  refKey={this.currentStore.id}
                  organisationName={this.currentStore.organisationName || 'Unknown Store'}
                  addressLine={this.currentStore.addressLine1 || ''}
                  zipCode={this.currentStore.zipCode || ''}
                  city={this.currentStore.city || ''}
                  distance={this.currentStore.distance}
                  brandsLogos={brandService.getLogosOrderedByPriority(this.currentStore?.crmAddress?.storeLocatorBrandIds)}
                  props={this.props?.ASL_StoreInfoCardProps}
                />
              </div>
            )}
          </div>
          <button
            class="top-1/2 right-1 absolute flex justify-center items-center bg-[--primary-button-background-color] rounded-full w-10 h-10 text-[--primary-button-text-color] -translate-y-1/2 translate-x-1/2 transform"
            onClick={() => this.openDetailsView()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </Host>
    );
  }
}
